import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { trackPageView, trackCTAClick } from '../utils/analytics';
import { 
  ArrowRight, Shield, Eye, CheckCircle, 
  AlertTriangle, TrendingDown, HelpCircle,
  Upload, Search, Target, Play,
  BarChart3, Zap, Lock, Users,
  ShoppingBag, Gauge, FileCheck, Lightbulb
} from 'lucide-react';
import { Button } from '../components/ui/button';

const PEDPage = () => {
  // Track page view
  useEffect(() => {
    trackPageView('/');
  }, []);

  // Problem points - emotional
  const problemPoints = [
    {
      icon: TrendingDown,
      title: "Raise prices?",
      fear: "What if customers leave?"
    },
    {
      icon: AlertTriangle,
      title: "Offer discounts?",
      fear: "What if margins collapse?"
    },
    {
      icon: HelpCircle,
      title: "Costs went up?",
      fear: "How much can I pass on?"
    }
  ];

  // How it works steps
  const howItWorks = [
    {
      step: "1",
      icon: Upload,
      title: "Connect Your Store",
      description: "Link Shopify in one click, or upload a simple CSV. No technical setup required."
    },
    {
      step: "2",
      icon: Search,
      title: "Instant Risk Scan",
      description: "We analyse your SKUs and flag which prices are safe, which are risky, and why."
    },
    {
      step: "3",
      icon: Target,
      title: "See Safe Ranges",
      description: "For every product, see exactly how far you can move prices without losing customers."
    },
    {
      step: "4",
      icon: Play,
      title: "Test Before You Act",
      description: "Simulate any price change and see projected impact — before it goes live."
    }
  ];

  // What you get free
  const freeFeatures = [
    {
      icon: Gauge,
      title: "Pricing Risk Score",
      description: "An overall health check of your current pricing"
    },
    {
      icon: AlertTriangle,
      title: "Top Risky SKUs",
      description: "The products most likely to hurt you if mispriced"
    },
    {
      icon: Shield,
      title: "Safe Price Ranges",
      description: "Know exactly where you can move without risk"
    },
    {
      icon: Lightbulb,
      title: "One Clear Action",
      description: "A single recommended next step, explained simply"
    }
  ];

  // Differentiation
  const comparisonPoints = [
    { ours: "Explainable insights", theirs: "Black-box algorithms" },
    { ours: "Safety-first approach", theirs: "Optimisation promises" },
    { ours: "Built for SMBs", theirs: "Enterprise complexity" },
    { ours: "See risk before acting", theirs: "Post-mortem analytics" }
  ];

  // Who it's for
  const idealFor = [
    "Shopify merchants scaling beyond £500k",
    "Owner-operators who make pricing decisions themselves",
    "Businesses feeling margin squeeze from rising costs",
    "Anyone tired of pricing by gut feel and hoping for the best"
  ];

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      {/* ============================================
          HERO SECTION
          Focus: Stop guessing, know what's safe
      ============================================ */}
      <section className="pt-40 pb-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2563EB]/5 via-transparent to-[#0A0F1C]" />
        
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F8FAFC] mb-6 leading-tight tracking-tight">
            Stop Guessing.
            <span className="block mt-2 bg-gradient-to-r from-[#2563EB] to-[#60A5FA] bg-clip-text text-transparent">
              Know What's Safe.
            </span>
          </h1>
          
          <p className="text-xl text-[#94A3B8] max-w-2xl mx-auto mb-4">
            See which price changes are safe — before your customers react.
          </p>
          
          <p className="text-lg text-[#F8FAFC]/80 max-w-xl mx-auto mb-10">
            PriceStrike shows you where you can move prices confidently, and warns you where you can't.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Link to="/pricing-check">
              <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-10 py-7 text-lg font-semibold rounded-xl w-full sm:w-auto shadow-lg shadow-[#2563EB]/25">
                Run Free Pricing Check
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
          
          <p className="text-[#94A3B8] text-sm">
            No sales call. No credit card. Results in minutes.
          </p>
        </div>
      </section>

      {/* ============================================
          PROBLEM SECTION (Emotional)
          Why pricing feels risky
      ============================================ */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#0A0F1C] to-[#111827]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-6">
              Pricing Isn't Hard. It's Risky.
            </h2>
            <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto">
              You know your products. You know your customers. But every time you think about changing prices, the same fears creep in.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {problemPoints.map((point, idx) => {
              const Icon = point.icon;
              return (
                <div key={idx} className="bg-[#0A0F1C] border border-[#94A3B8]/20 rounded-2xl p-8 text-center">
                  <div className="w-14 h-14 bg-orange-500/10 border border-orange-500/30 rounded-xl flex items-center justify-center mx-auto mb-5">
                    <Icon className="w-7 h-7 text-orange-400" />
                  </div>
                  <h3 className="text-xl font-bold text-[#F8FAFC] mb-2">{point.title}</h3>
                  <p className="text-[#94A3B8] italic">"{point.fear}"</p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-[#94A3B8]">
              This hesitation isn't weakness. It's rational.
              <span className="block mt-2 text-[#F8FAFC] font-medium">
                You just need to see the risk before you act.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* ============================================
          NARRATIVE SHIFT
          Reframe: "Is this safe?" not "What price?"
      ============================================ */}
      <section className="py-20 px-6 bg-[#111827]">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-[#2563EB]/10 to-transparent border border-[#2563EB]/30 rounded-3xl p-10 md:p-14">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 bg-[#2563EB]/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Eye className="w-8 h-8 text-[#2563EB]" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-4">
                  The Question Isn't "What Price Should I Set?"
                </h2>
                <p className="text-xl text-[#F8FAFC] mb-4">
                  It's: <span className="text-[#2563EB] font-semibold">"Is this change safe?"</span>
                </p>
                <p className="text-lg text-[#94A3B8]">
                  PriceStrike doesn't tell you what to do. It shows you what's safe — so you can decide with confidence, not anxiety.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          SOLUTION SECTION
          What PriceStrike actually does
      ============================================ */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#111827] to-[#0A0F1C]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-6">
              See Pricing Risk Before You Act
            </h2>
            <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto">
              PriceStrike analyses your sales data to show you exactly where you can move prices safely — and warns you where you can't.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-8">
              <div className="w-12 h-12 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center justify-center mb-5">
                <Shield className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-[#F8FAFC] mb-3">Safe vs Risky Prices</h3>
              <p className="text-[#94A3B8]">
                Every product gets a clear signal: green for safe to adjust, amber for caution, red for risk.
              </p>
            </div>

            <div className="bg-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-8">
              <div className="w-12 h-12 bg-orange-500/10 border border-orange-500/30 rounded-xl flex items-center justify-center mb-5">
                <AlertTriangle className="w-6 h-6 text-orange-400" />
              </div>
              <h3 className="text-xl font-bold text-[#F8FAFC] mb-3">Margin Leak Alerts</h3>
              <p className="text-[#94A3B8]">
                Spot products where you're leaving money behind — or where discounts are quietly eating your profit.
              </p>
            </div>

            <div className="bg-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-8">
              <div className="w-12 h-12 bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-xl flex items-center justify-center mb-5">
                <Play className="w-6 h-6 text-[#2563EB]" />
              </div>
              <h3 className="text-xl font-bold text-[#F8FAFC] mb-3">Scenario Testing</h3>
              <p className="text-[#94A3B8]">
                Try any price change in a simulator first. See projected impact before anything goes live.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-[#94A3B8]">
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              No spreadsheets
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              No guesswork
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              No black box
            </span>
          </div>
        </div>
      </section>

      {/* ============================================
          HOW IT WORKS (PLG Flow)
          Time to value: under 10 minutes
      ============================================ */}
      <section className="py-20 px-6 bg-[#0A0F1C]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-6">
              Get Clarity in Under 10 Minutes
            </h2>
            <p className="text-lg text-[#94A3B8]">
              No onboarding calls. No setup fees. Just connect and see.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="relative">
                  <div className="bg-[#111827] border border-[#2563EB]/20 rounded-2xl p-6 h-full">
                    <div className="absolute -top-4 -left-2 w-10 h-10 bg-[#2563EB] rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {item.step}
                    </div>
                    <div className="pt-4">
                      <div className="w-12 h-12 bg-[#2563EB]/10 rounded-xl flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-[#2563EB]" />
                      </div>
                      <h3 className="text-lg font-bold text-[#F8FAFC] mb-2">{item.title}</h3>
                      <p className="text-[#94A3B8] text-sm">{item.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================
          WHAT YOU GET FOR FREE
          Concrete outputs
      ============================================ */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#0A0F1C] to-[#111827]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-6">
              What You Get — Free
            </h2>
            <p className="text-lg text-[#94A3B8]">
              Your first pricing risk scan is completely free. Here's exactly what you'll see:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {freeFeatures.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div key={idx} className="flex items-start gap-4 bg-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6">
                  <div className="w-12 h-12 bg-[#2563EB]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-[#2563EB]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#F8FAFC] mb-1">{feature.title}</h3>
                    <p className="text-[#94A3B8] text-sm">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center">
            <p className="text-[#94A3B8] mb-6">
              Paid plans unlock execution tools — but the insight is yours to keep, free.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================
          DIFFERENTIATION BLOCK
          Us vs traditional tools
      ============================================ */}
      <section className="py-20 px-6 bg-[#111827]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-6">
              Not Another Black Box
            </h2>
            <p className="text-lg text-[#94A3B8]">
              Most pricing tools promise "optimisation" but can't explain why. We show you the logic, every time.
            </p>
          </div>

          <div className="bg-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl overflow-hidden">
            <div className="grid grid-cols-2">
              <div className="p-6 bg-[#2563EB]/10 border-b border-r border-[#2563EB]/20">
                <h4 className="font-bold text-[#2563EB] text-sm uppercase tracking-wide">PriceStrike</h4>
              </div>
              <div className="p-6 border-b border-[#2563EB]/20">
                <h4 className="font-bold text-[#94A3B8] text-sm uppercase tracking-wide">Traditional Tools</h4>
              </div>
            </div>
            
            {comparisonPoints.map((point, idx) => (
              <div key={idx} className="grid grid-cols-2">
                <div className="p-6 bg-[#2563EB]/5 border-b border-r border-[#2563EB]/10 last:border-b-0">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-[#F8FAFC]">{point.ours}</span>
                  </div>
                </div>
                <div className="p-6 border-b border-[#2563EB]/10 last:border-b-0">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full border-2 border-[#94A3B8]/30 flex-shrink-0" />
                    <span className="text-[#94A3B8]">{point.theirs}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          WHO IT'S FOR
          Clear ICP
      ============================================ */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#111827] to-[#0A0F1C]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-6">
              Built for Business Owners Who Price
            </h2>
            <p className="text-lg text-[#94A3B8]">
              Not enterprise procurement teams. Not pricing PhDs. Just operators who want clarity.
            </p>
          </div>

          <div className="bg-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-8 md:p-10">
            <h3 className="text-xl font-bold text-[#F8FAFC] mb-6">PriceStrike is for you if:</h3>
            <ul className="space-y-4">
              {idealFor.map((item, idx) => (
                <li key={idx} className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-[#2563EB] flex-shrink-0 mt-0.5" />
                  <span className="text-lg text-[#F8FAFC]">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ============================================
          FINAL CTA
          Reinforce safety
      ============================================ */}
      <section className="py-24 px-6 bg-[#0A0F1C]">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-[#2563EB]/20 to-[#0A0F1C] border border-[#2563EB]/30 rounded-3xl p-10 md:p-14 text-center">
            <Shield className="w-14 h-14 text-[#2563EB] mx-auto mb-6" />
            
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-6">
              See Where Your Pricing Is Safe
            </h2>
            
            <p className="text-xl text-[#94A3B8] mb-10">
              Before your customers tell you the hard way.
            </p>
            
            <Link to="/pricing-check" onClick={() => trackCTAClick('Run Free Pricing Check - Bottom CTA', '/')}>
              <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-12 py-7 text-lg font-semibold rounded-xl shadow-lg shadow-[#2563EB]/25">
                Run Free Pricing Check
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            
            <p className="text-[#94A3B8] text-sm mt-6">
              No sales call. No credit card. Results in minutes.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PEDPage;
